import {
  CHUNK_CONTENT_TYPE,
  UPLOAD_OFFSET_HEADER,
  type TransferFile,
  type UploadTicket,
} from "@airspace/shared";
import { ApiRequestError } from "./client.js";

/**
 * Client-Seite des Dateiaustauschs.
 *
 * Weder beim Hoch- noch beim Herunterladen wird eine Datei vollstaendig in den
 * Arbeitsspeicher gelesen. Der Upload schneidet Ausschnitte aus dem `File`
 * (der Browser liest sie erst beim Senden von der Platte), der Download
 * schreibt den Antwortstrom direkt in eine vom Nutzer gewaehlte Datei.
 */

// --- REST --------------------------------------------------------------------

async function json<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: body === undefined ? {} : { "content-type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text.length > 0 ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiRequestError(
      response.status,
      error?.code ?? "unknown_error",
      error?.message ?? "Unerwarteter Fehler.",
    );
  }
  return payload as T;
}

export const transferApi = {
  list: () => json<{ files: TransferFile[] }>("GET", "/api/throw/files"),

  offer: (file: File) =>
    json<{ file: TransferFile }>("POST", "/api/throw/files", {
      name: file.name,
      size: file.size,
      mimeType: file.type || null,
      lastModified: file.lastModified,
    }),

  accept: (fileId: string) => json<void>("POST", `/api/throw/files/${fileId}/accept`),
  reject: (fileId: string) => json<void>("POST", `/api/throw/files/${fileId}/reject`),
  cancel: (fileId: string) => json<void>("POST", `/api/throw/files/${fileId}/cancel`),

  createUpload: (fileId: string) =>
    json<UploadTicket>("POST", `/api/throw/files/${fileId}/upload`),

  uploadOffset: (uploadId: string) =>
    json<{ offset: number; size: number }>("GET", `/api/throw/uploads/${uploadId}`),
};

/** Zugang zu den Throw-Endpunkten. */
export function throwUploadBackend(fileId: string): UploadBackend {
  return {
    createTicket: () => transferApi.createUpload(fileId),
    chunkUrl: (uploadId) => `/api/throw/uploads/${encodeURIComponent(uploadId)}`,
    currentOffset: async (uploadId) => (await transferApi.uploadOffset(uploadId)).offset,
  };
}

export const downloadUrl = (fileId: string): string =>
  `/api/throw/files/${encodeURIComponent(fileId)}/download`;

/** Alle empfangenen Dateien als ein ZIP-Archiv. */
export const BULK_DOWNLOAD_URL = "/api/throw/files/download-all";

// --- Upload ------------------------------------------------------------------

export type UploadState = "idle" | "running" | "paused" | "done" | "error";

export interface UploadProgress {
  state: UploadState;
  sentBytes: number;
  totalBytes: number;
  error?: string;
}

/**
 * Laedt eine Datei in Ausschnitten hoch und kann jederzeit angehalten,
 * fortgesetzt oder abgebrochen werden.
 *
 * Der Server ist die einzige Instanz, die weiss, wie weit der Upload ist. Nach
 * jeder Stoerung wird der Offset dort erfragt, statt einem lokalen Zaehler zu
 * vertrauen -- der koennte einen abgebrochenen Chunk mitgezaehlt haben, der nie
 * ankam.
 */
/**
 * Woher der Uploader seinen Zugang bekommt und wohin er die Ausschnitte
 * schickt.
 *
 * Throw und Vault benutzen dasselbe Protokoll, aber verschiedene Endpunkte.
 * Statt den Uploader zweimal zu schreiben, bekommt er diese beiden Angaben von
 * aussen -- ein Fehler im Wiederaufnahme-Verhalten kann so nicht in einer der
 * beiden Fassungen ueberleben.
 */
export interface UploadBackend {
  /** Meldet den Upload an. Idempotent: liefert bei Wiederholung denselben. */
  createTicket: () => Promise<UploadTicket>;
  /** Adresse, an die Ausschnitte gehen. */
  chunkUrl: (uploadId: string) => string;
  /** Fragt den tatsaechlichen Stand ab. */
  currentOffset: (uploadId: string) => Promise<number>;
}

export class ChunkUploader {
  readonly fileId: string;
  readonly #file: File;
  readonly #backend: UploadBackend;
  readonly #onProgress: (progress: UploadProgress) => void;

  #state: UploadState = "idle";
  #sent = 0;
  #controller: AbortController | null = null;
  /** Wird gesetzt, solange der Uploader auf ein Fortsetzen wartet. */
  #resume: (() => void) | null = null;

  constructor(
    fileId: string,
    file: File,
    backend: UploadBackend,
    onProgress: (progress: UploadProgress) => void,
  ) {
    this.fileId = fileId;
    this.#file = file;
    this.#backend = backend;
    this.#onProgress = onProgress;
  }

  get state(): UploadState {
    return this.#state;
  }

  /** Haelt nach dem laufenden Chunk an. */
  pause(): void {
    if (this.#state !== "running") return;
    this.#setState("paused");
  }

  resume(): void {
    if (this.#state !== "paused") return;
    this.#setState("running");
    this.#resume?.();
    this.#resume = null;
  }

  /** Bricht sofort ab und verwirft den laufenden Chunk. */
  abort(): void {
    this.#controller?.abort();
    this.#resume?.();
    this.#resume = null;
  }

  async run(): Promise<void> {
    this.#setState("running");

    try {
      const ticket = await this.#backend.createTicket();
      let offset = ticket.offset;
      this.#sent = offset;
      this.#report();

      // Wiederholungen mit wachsender Wartezeit: eine kurze Netzstoerung soll
      // den Transfer nicht beenden, ein dauerhaft toter Server ihn aber nicht
      // ewig blockieren.
      let attempt = 0;

      while (offset < this.#file.size) {
        if (this.#state === "paused") {
          await new Promise<void>((resolve) => {
            this.#resume = resolve;
          });
          // Waehrend des Wartens kann `abort()` den Zustand veraendert haben.
          // TypeScript sieht das nicht, weil es keine Zuweisung von aussen
          // erkennt -- der Getter macht die Absicht explizit.
          if (this.state !== "running") return;
        }

        const end = Math.min(offset + ticket.chunkSize, this.#file.size);
        // `slice` liest nichts -- der Browser holt die Bytes erst beim Senden
        // von der Platte. Auch eine 40-GB-Datei belegt hier keinen Speicher.
        const chunk = this.#file.slice(offset, end);

        this.#controller = new AbortController();
        let response: Response;
        try {
          response = await fetch(this.#backend.chunkUrl(ticket.uploadId), {
            method: "PATCH",
            headers: {
              "content-type": CHUNK_CONTENT_TYPE,
              [UPLOAD_OFFSET_HEADER]: String(offset),
            },
            body: chunk,
            signal: this.#controller.signal,
          });
        } catch (error) {
          if (this.#controller.signal.aborted) return;

          attempt += 1;
          if (attempt > 6) throw new Error("Die Verbindung zum Server bricht wiederholt ab.");
          await delay(Math.min(1000 * 2 ** (attempt - 1), 15_000));

          // Nach einer Stoerung gilt nur, was der Server bestaetigt.
          offset = await this.#backend.currentOffset(ticket.uploadId);
          this.#sent = offset;
          this.#report();
          continue;
        }

        if (response.status === 409) {
          // Der Server steht woanders -- nachziehen statt neu beginnen.
          offset = await this.#backend.currentOffset(ticket.uploadId);
          this.#sent = offset;
          this.#report();
          continue;
        }

        if (!response.ok) {
          const body = (await response.json().catch(() => null)) as
            | { error?: { message?: string } }
            | null;
          throw new Error(body?.error?.message ?? `Upload fehlgeschlagen (${response.status}).`);
        }

        attempt = 0;
        const result = (await response.json()) as { offset: number };
        offset = result.offset;
        this.#sent = offset;
        this.#report();
      }

      this.#setState("done");
    } catch (error) {
      this.#state = "error";
      this.#onProgress({
        state: "error",
        sentBytes: this.#sent,
        totalBytes: this.#file.size,
        error: error instanceof Error ? error.message : "Upload fehlgeschlagen.",
      });
    }
  }

  #setState(state: UploadState): void {
    this.#state = state;
    this.#report();
  }

  #report(): void {
    this.#onProgress({
      state: this.#state,
      sentBytes: this.#sent,
      totalBytes: this.#file.size,
    });
  }
}

// --- Download ----------------------------------------------------------------

export interface DownloadProgress {
  receivedBytes: number;
  totalBytes: number;
}

/** Ob der Browser Downloads direkt in eine gewaehlte Datei schreiben kann. */
export function supportsDirectSave(): boolean {
  return typeof window.showSaveFilePicker === "function";
}

/**
 * Laedt eine Datei herunter und schreibt sie direkt auf die Platte.
 *
 * Der Antwortstrom geht ueber `pipeTo` unmittelbar in die Zieldatei -- die
 * Datei landet nie als Ganzes im Arbeitsspeicher. Ohne das waere bei einigen
 * Gigabyte der Tab tot.
 *
 * Steht die File System Access API nicht zur Verfuegung, faellt die Funktion
 * auf den gewoehnlichen Browser-Download zurueck. Der funktioniert, zeigt aber
 * keinen Fortschritt in der Anwendung.
 */
export interface DownloadTarget {
  url: string;
  /** Vorschlag fuer den Speichern-Dialog. */
  name: string;
  /**
   * Erwartete Gesamtgroesse fuer die Fortschrittsanzeige.
   *
   * Beim Sammeldownload nur eine Schaetzung: die Summe der Dateigroessen ohne
   * den Mehraufwand des Archivformats.
   */
  totalBytes: number;
}

export async function downloadToDisk(
  target: DownloadTarget,
  onProgress: (progress: DownloadProgress) => void,
  signal?: AbortSignal,
): Promise<"saved" | "delegated"> {
  const url = target.url;

  if (!supportsDirectSave()) {
    // Rueckfall: der Browser uebernimmt. Kein Fortschritt, aber es kommt an.
    const link = document.createElement("a");
    link.href = url;
    link.download = target.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    return "delegated";
  }

  const handle = await window.showSaveFilePicker!({ suggestedName: target.name });
  const writable = await handle.createWritable();

  try {
    // `signal: undefined` ist unter exactOptionalPropertyTypes kein gueltiger
    // Wert -- die Eigenschaft muss ganz fehlen, wenn es kein Signal gibt.
    const response = await fetch(url, signal ? { signal } : {});
    if (!response.ok || !response.body) {
      throw new Error(`Download fehlgeschlagen (${response.status}).`);
    }

    // Der Server kuendigt die Groesse an, sobald er sie kennt. Beim Archiv
    // laesst sie sich vorab ausrechnen -- damit wird ein abgebrochener
    // Download hier ueberhaupt erst erkennbar.
    const angekuendigt = Number(response.headers.get("content-length"));
    const erwartet = Number.isFinite(angekuendigt) && angekuendigt > 0 ? angekuendigt : null;

    let received = 0;
    // Zaehlt die Bytes im Vorbeifliessen, ohne sie zu sammeln.
    const counter = new TransformStream<Uint8Array, Uint8Array>({
      transform(chunk, controller) {
        received += chunk.byteLength;
        onProgress({ receivedBytes: received, totalBytes: erwartet ?? target.totalBytes });
        controller.enqueue(chunk);
      },
      flush() {
        // Reisst die Verbindung mittendrin ab, endet der Strom regulaer --
        // ohne diese Pruefung landete ein unvollstaendiges Archiv als
        // scheinbar fertige Datei auf der Platte.
        if (erwartet !== null && received !== erwartet) {
          throw new Error(
            `Der Download ist unvollständig (${received} von ${erwartet} Bytes). Die Datei wurde verworfen.`,
          );
        }
      },
    });

    await response.body.pipeThrough(counter).pipeTo(writable);
    return "saved";
  } catch (error) {
    // Verwerfen statt schliessen: `close()` wuerde die angefangene Datei
    // festschreiben, und der Nutzer haette ein halbes Archiv auf der Platte,
    // das sich nicht oeffnen laesst -- ohne jeden Hinweis darauf.
    await writable.abort().catch(() => undefined);
    throw error;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
